import { Alert, Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { SMS_MAX_LENGTH } from "@sinch/core/config/globals";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SegmentedMessage } from "sms-segments-calculator/dist/libs/SegmentedMessage";
import { TextEditor, TextEditorProps } from "../TextEditor/TextEditor";

export const SMSMessageInput = ({
  value,
  recipientCount,
  placeholders = [],
  ...props
}: Omit<TextEditorProps, "placeholders"> & {
  recipientCount: number;
  placeholders?: { label: ReactNode; value: string; size: number }[];
}) => {
  const { t } = useTranslation();

  const message = value ?? "";
  const fullMessage = placeholders.reduce(
    (acc, placeholder) => acc.replaceAll(`[[${placeholder.value}]]`, "€".repeat(placeholder.size)),
    message
  );
  const segmentedMessage = new SegmentedMessage(fullMessage);
  const characterCount = useMemo(
    () => segmentedMessage.encodedChars.reduce((chars, encodedChar) => chars + encodedChar.codeUnits.length, 0),
    [segmentedMessage.encodedChars]
  );

  const hasSomePlaceholders = placeholders.some((placeholder) => message.indexOf(`[[${placeholder.value}]]`) !== -1);

  return (
    <Stack gap={1}>
      <TextEditor
        FormHelperTextProps={{ component: "span" }}
        fullWidth
        helperText={
          <Stack direction="row" justifyContent="space-between">
            <Box component="span" sx={{ color: characterCount > SMS_MAX_LENGTH ? "error.main" : undefined }}>
              {t("Messages.SMSMessageCharactersOf", {
                characters: characterCount,
                limit: SMS_MAX_LENGTH,
              })}
            </Box>
            <span>
              {t("Messages.segmentsCount", {
                segments: (
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    {segmentedMessage.segmentsCount}
                  </Typography>
                ),
              })}
            </span>
          </Stack>
        }
        label={t("Messages.messageInputLabel")}
        minRows={4}
        multiline
        placeholders={placeholders}
        plainText
        value={value}
        variant="plain"
        {...props}
      />
      <Alert icon={false} severity="info" variant="outlined">
        {t("Messages.SMSSegmentsInfo", {
          sendSms: segmentedMessage.segmentsCount * (recipientCount || 1),
          perRecipient: segmentedMessage.segmentsCount,
        })}
      </Alert>
      {(hasSomePlaceholders || segmentedMessage.encodingName !== "GSM-7") && (
        <Alert severity="warning" variant="standard">
          {t("Messages.SMSNonStandardCharactersInMessage")}
          {hasSomePlaceholders && (
            <>
              <br />
              <br />
              {t("Messages.SMSPlaceholders")}
            </>
          )}
        </Alert>
      )}
    </Stack>
  );
};
